import type { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import axios, { AxiosError } from "axios";
import { ROUTES } from "@/router";
import { useAppStore } from "@/store/app/slice";
import { Environment, getApiUrl, getEnvironment } from "@/utils/getEnvironment";
import { ApiErrorCode } from "../constants";
import { onRefreshToken } from "./onRefreshToken";

const axiosInstance = axios.create({
  baseURL: getApiUrl(),
  withCredentials: getEnvironment() !== Environment.Local,
});

const onRequest = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  const accessToken: string = useAppStore.getState().accessToken;

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  // Extract tenant from URL path (e.g., "/Tenant%20Name/projects" → "Tenant Name")
  const pathSegments = window.location.pathname.split("/");
  const tenantSegment = pathSegments[1];

  const excludedSegments = ["admin", ROUTES.AUTH];

  if (tenantSegment && !excludedSegments.includes(tenantSegment)) {
    const tenantName = decodeURIComponent(tenantSegment);
    config.headers["X-Tenant-Name"] = tenantName;
  }

  return config;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error);
};

const onResponse = (response: AxiosResponse): AxiosResponse => {
  return response;
};

const onResponseError = (error: AxiosError) => {
  if (error?.response?.status === ApiErrorCode.Unauthorized) {
    return onRefreshToken(error)
      .then((config) => {
        return axiosInstance.request(config);
      })
      .catch((e: Error | AxiosError) => {
        if (
          e instanceof AxiosError &&
          e.response?.status === ApiErrorCode.Unauthorized
        ) {
          window.location.href = `/${ROUTES.SIGN_OUT}`;
        }
        return Promise.reject(e);
      });
  }

  return Promise.reject(error);
};

axiosInstance.interceptors.request.use(onRequest, onRequestError);
axiosInstance.interceptors.response.use(onResponse, onResponseError);

export { axiosInstance };
